import { useState } from 'react';
import { motion } from 'framer-motion';
import { TABLE_SIZES, STAKES } from '@durakapp/shared';
import type { Stake, TableSize } from '@durakapp/shared';
import { useLobby } from '../stores/lobby.js';
import { useAuth } from '../stores/auth.js';
import { useToast } from '../stores/toast.js';
import { CardBack } from '../components/Card.js';
import { ChipIcon } from '../components/ChipIcon.js';
import { TopupSheet } from '../components/TopupSheet.js';
import { SideMenu } from '../components/SideMenu.js';
import { haptic } from '../lib/telegram.js';
import { emitJoinQueue, emitLeaveQueue } from '../lib/gameSocket.js';
import { useTopup } from '../lib/useTopup.js';
import { t } from '../lib/i18n.js';

interface StakeMeta {
  value: string;
  caption: string;
  showStar: boolean;
}

const STAKE_META: Record<Stake, StakeMeta> = {
  0: { value: 'Free', caption: 'обучение', showStar: false },
  10: { value: '10', caption: 'мягкий вход', showStar: true },
  50: { value: '50', caption: 'обычный стол', showStar: true },
  100: { value: '100', caption: 'для опытных', showStar: true },
  500: { value: '500', caption: 'VIP', showStar: true },
};

export function Lobby() {
  const { stake, tableSize, status, setStake, setTableSize, setStatus } = useLobby();
  const user = useAuth((s) => s.user);
  const pushToast = useToast((s) => s.push);
  const { topup } = useTopup();
  const [topupOpen, setTopupOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isQueued = status === 'queueing' || status === 'queued';

  const tryJoin = async (currentStake: Stake): Promise<void> => {
    setStatus('queueing');
    try {
      await emitJoinQueue({ stake: currentStake, tableSize });
    } catch (err) {
      setStatus('idle');
      const message = err instanceof Error ? err.message : 'Не удалось встать в очередь';
      const m = /^INSUFFICIENT_BALANCE:(\d+)$/.exec(message);
      if (m && m[1]) {
        const needed = Number.parseInt(m[1], 10);
        pushToast(t.paymentNeeded(needed), 'info');
        const outcome = await topup(needed, t.paymentCredited);
        if (outcome === 'paid') await tryJoin(currentStake);
        return;
      }
      pushToast(message, 'error');
    }
  };

  const onPrimary = async (): Promise<void> => {
    haptic('medium');
    if (isQueued) {
      emitLeaveQueue();
      setStatus('idle');
      return;
    }
    await tryJoin(stake);
  };

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 px-5 pb-32 pt-4">
      <header className="flex items-center justify-between">
        <button
          aria-label="Открыть меню"
          onClick={() => {
            haptic('light');
            setMenuOpen(true);
          }}
          className="grid h-10 w-10 place-items-center rounded-full"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gold">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="heading-display text-2xl">DURAK</h1>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setTopupOpen(true);
          }}
          className="pill-glass text-sm"
          aria-label="Пополнить баланс"
        >
          <span>{user?.starsBalance.toLocaleString('ru-RU') ?? '0'}</span>
          <ChipIcon size={14} />
          <span className="ml-1 text-ivory/55">+</span>
        </button>
      </header>

      <div className="relative -mx-3 flex h-32 items-center justify-center overflow-hidden">
        {[0, 1, 2, 3].map((i) => {
          const t = i / 3 - 0.5;
          return (
            <motion.div
              key={i}
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.05 * i, duration: 0.28 }}
              className="absolute"
              style={{ transform: `translate(${t * 130}px, ${Math.abs(t) * 6}px) rotate(${t * 12}deg)` }}
            >
              <CardBack size="md" />
            </motion.div>
          );
        })}
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="heading-display text-xl">Стол</h2>
        <div
          className="flex rounded-full p-1"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
          }}
        >
          {TABLE_SIZES.map((n) => (
            <button
              key={n}
              disabled={isQueued}
              onClick={() => {
                haptic('light');
                setTableSize(n as TableSize);
              }}
              className="relative flex-1 rounded-full py-2 text-sm transition-colors disabled:opacity-50"
              style={{
                background: tableSize === n ? 'var(--color-gold)' : 'transparent',
                color: tableSize === n ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                fontWeight: tableSize === n ? 700 : 500,
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="heading-display text-xl">Ставка</h2>
        <div className="flex flex-col gap-2">
          {STAKES.map((s) => {
            const meta = STAKE_META[s as Stake];
            const isSelected = stake === s;
            const isVip = s === 500;
            return (
              <button
                key={s}
                disabled={isQueued}
                onClick={() => {
                  haptic('light');
                  setStake(s as Stake);
                }}
                className="relative flex items-center justify-between gap-3 rounded-full px-6 py-4 transition-all disabled:opacity-50"
                style={{
                  background: isSelected
                    ? 'color-mix(in oklab, var(--color-tg-blue) 18%, var(--color-glass))'
                    : isVip
                      ? 'linear-gradient(90deg, color-mix(in oklab, var(--color-gold) 20%, var(--color-glass)) 0%, color-mix(in oklab, var(--color-gold) 35%, var(--color-glass)) 100%)'
                      : 'var(--color-glass)',
                  border: `1px solid color-mix(in oklab, var(--color-gold) ${isSelected ? '90%' : isVip ? '70%' : '35%'}, transparent)`,
                  boxShadow: isSelected
                    ? '0 0 0 2px color-mix(in oklab, var(--color-gold) 60%, transparent)'
                    : 'none',
                }}
              >
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--color-ivory)' }}>
                    {meta.value}
                  </span>
                  {meta.showStar && <ChipIcon size={20} />}
                </div>
                <span className="text-xs text-ivory/60">{meta.caption}</span>
              </button>
            );
          })}
        </div>
      </section>

      <p className="text-center text-xs text-ivory/50">среднее ожидание · 12 сек</p>

      {/* Floating CTA — sits above the bottom-nav so it's visible without scrolling. */}
      <div
        className="fixed inset-x-0 z-30 mx-auto w-full max-w-[480px] px-4 pb-3 pt-4"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 64px)',
          background:
            'linear-gradient(180deg, transparent 0%, color-mix(in oklab, var(--color-felt-deep) 75%, transparent) 35%, color-mix(in oklab, var(--color-felt-deep) 95%, transparent) 100%)',
          pointerEvents: 'none',
        }}
      >
        <button
          onClick={onPrimary}
          className="btn-primary w-full text-base"
          style={{ pointerEvents: 'auto' }}
        >
          {isQueued ? 'Отменить поиск' : 'Найти игру'}
          {!isQueued && <ChipIcon size={16} />}
        </button>
      </div>

      <TopupSheet open={topupOpen} onClose={() => setTopupOpen(false)} />
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
  );
}
